import { MarkerDimension, SegObjectPropertyMap } from '../case/case.state'
import { DomainInfo } from './plot-data.state'
import { isMappingProperty } from './mapping-property'

/** Convert a normalized bin value (-1..1) to physical units for a marker domain. */
export function denormalizeValue(norm: number, domain: DomainInfo): number {
    return ((norm + 1) * (domain.upperBound - domain.lowerBound)) / 2 + domain.lowerBound
}

/** Convert a physical value to normalized coordinates for a marker domain. */
export function normalizeValue(physical: number, domain: DomainInfo): number {
    const range = domain.upperBound - domain.lowerBound
    if (range === 0) return 0
    return (2 * (physical - domain.lowerBound)) / range - 1
}

/** Union marker bounds across loaded maps for a shared plot axis. */
export function mergeDomains(domains: DomainInfo[]): DomainInfo {
    return {
        lowerBound: Math.min(...domains.map(d => d.lowerBound)),
        upperBound: Math.max(...domains.map(d => d.upperBound)),
    }
}

/**
 * Pick X/Y property names shared across loaded intensity maps.
 * Ignores synthetic "mapping" (color-only). Uses Set for membership; only scans
 * names when picking a replacement default.
 */
export function resolveAxisNames(
    currentX: string | undefined,
    currentY: string | undefined,
    sharedNames: string[],
    sharedSet: Set<string> = new Set(sharedNames)
): { markerXName: string | undefined; markerYName: string | undefined } {
    if (sharedNames.length < 2) {
        return { markerXName: undefined, markerYName: undefined }
    }

    const keepX = !!currentX && !isMappingProperty(currentX) && sharedSet.has(currentX)
    const keepY =
        !!currentY &&
        !isMappingProperty(currentY) &&
        sharedSet.has(currentY) &&
        currentY !== currentX

    if (keepX && keepY) {
        return { markerXName: currentX, markerYName: currentY }
    }
    if (keepX) {
        const markerYName = findOtherAxisName(sharedNames, currentX!)
        return { markerXName: currentX, markerYName }
    }
    if (keepY) {
        const markerXName = findOtherAxisName(sharedNames, currentY!)
        return { markerXName, markerYName: currentY }
    }

    const markerXName = findOtherAxisName(sharedNames)
    const markerYName = markerXName ? findOtherAxisName(sharedNames, markerXName) : undefined
    return { markerXName, markerYName }
}

function findOtherAxisName(sharedNames: string[], exclude?: string): string | undefined {
    for (const name of sharedNames) {
        if (isMappingProperty(name)) continue
        if (exclude != null && name === exclude) continue
        return name
    }
    return undefined
}

export function resolveMarkerAxesForLoadedMaps(
    loadedMaps: SegObjectPropertyMap[],
    markerDimensionsByMapId: Record<number, MarkerDimension[]>,
    markerXName: string,
    markerYName: string
): { markerX: MarkerDimension; markerY: MarkerDimension } | null {
    if (loadedMaps.length === 0) return null
    if (isMappingProperty(markerXName) || isMappingProperty(markerYName)) {
        return null
    }

    let markerX: MarkerDimension | null = null
    let markerY: MarkerDimension | null = null

    for (const map of loadedMaps) {
        const markers =
            markerDimensionsByMapId[Number(map.id)] ?? markerDimensionsByMapId[map.id]
        if (!markers) continue
        if (!markerX) {
            const found = markers.find(m => m.name === markerXName)
            if (found) {
                markerX = { ...found, segobjectpropertymap: found.segobjectpropertymap ?? map }
            }
        }
        if (!markerY) {
            const found = markers.find(m => m.name === markerYName)
            if (found) {
                markerY = { ...found, segobjectpropertymap: found.segobjectpropertymap ?? map }
            }
        }
        if (markerX && markerY) {
            return { markerX, markerY }
        }
    }

    return markerX && markerY ? { markerX, markerY } : null
}

/** True when this axis marker is still present on a currently loaded map (same name + source_bin). */
export function markerAxisBelongsToLoadedMaps(
    marker: MarkerDimension | null | undefined,
    loadedMaps: SegObjectPropertyMap[],
    markerDimensionsByMapId?: Record<number, MarkerDimension[]>
): boolean {
    if (!marker?.name || isMappingProperty(marker.name)) return false

    const mapId = marker.segobjectpropertymap?.id
    // Coerce ids — map.id / segobjectpropertymap.id may be number vs string.
    if (
        mapId != null &&
        !loadedMaps.some(map => Number(map.id) === Number(mapId))
    ) {
        return false
    }

    // Prefer an exact source_bin match so same-named TSNE/UMAP props from an unloaded
    // map are not kept after switching intensity maps.
    if (marker.source_bin && markerDimensionsByMapId) {
        return loadedMaps.some(map => {
            const markers =
                markerDimensionsByMapId[Number(map.id)] ?? markerDimensionsByMapId[map.id]
            return !!markers?.some(
                m => m.name === marker.name && m.source_bin === marker.source_bin
            )
        })
    }

    if (mapId != null) return true
    // No map id / source_bin: cannot prove the marker belongs to a loaded map.
    return false
}
